import {
  Icon,
  IconType,
  Tooltip,
  TooltipContent,
  TooltipGroup,
  TooltipProvider,
  TooltipTrigger,
} from "@platform/ui/components";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { useSuspenseQuery } from "@tanstack/react-query";
import * as React from "react";
import { twMerge } from "tailwind-merge";
import { useUpdateThemePreference } from "~/shared/api/users/users.mutations";
import { USERS_QUERIES } from "~/shared/api/users/users.queries";
import { useUserTheme, type Theme } from "~/shared/components/UserThemePreference";

export function UserThemePreferenceDropdown() {
  const theme = useUserTheme();
  const { data: user } = useSuspenseQuery(USERS_QUERIES.profile());
  const updateThemePreferenceMutation = useUpdateThemePreference(user.id);

  return (
    <DropdownMenuPrimitive.DropdownMenuRadioGroup
      value={theme}
      onValueChange={(value) => {
        updateThemePreferenceMutation.mutate({ theme: value as Theme });
      }}
    >
      <div className="flex items-center justify-between gap-2 px-2 py-1">
        <DropdownMenuPrimitive.DropdownMenuLabel className="text-ds-text-primary flex-1 text-sm">
          Theme
        </DropdownMenuPrimitive.DropdownMenuLabel>
        <TooltipGroup className="w-fit">
          <TooltipProvider delayDuration={0}>
            <div className="border-ds-stroke-primary bg-ds-surface-1 inline-flex items-center gap-1 rounded-sm border p-1 shadow-2xs">
              <ThemeOption value="light">
                <ThemeIcon active="sun-fill" inactive="sun-line" />
              </ThemeOption>
              <ThemeOption value="dark">
                <ThemeIcon active="moon-fill" inactive="moon-line" />
              </ThemeOption>
              <ThemeOption value="system">
                <ThemeIcon active="progress-4-fill" inactive="progress-4-line" />
              </ThemeOption>
            </div>
          </TooltipProvider>
        </TooltipGroup>
      </div>
    </DropdownMenuPrimitive.DropdownMenuRadioGroup>
  );
}

function ThemeOption({ value, children }: { value: Theme; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <DropdownMenuPrimitive.DropdownMenuRadioItem
          onSelect={(e) => {
            e.preventDefault();
          }}
          value={value}
          className={twMerge(
            "group/item grid size-4.5 place-items-center rounded-full outline-hidden",
            "text-ds-icon-tertiary data-[highlighted]:text-ds-icon-primary aria-checked:text-ds-icon-primary transition-colors",
          )}
        >
          {children}
        </DropdownMenuPrimitive.DropdownMenuRadioItem>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        className="slide-in-from-bottom-0! fade-in-100 zoom-in-100 capitalize"
        sideOffset={4}
      >
        {value}
      </TooltipContent>
    </Tooltip>
  );
}

function ThemeIcon({ active, inactive }: { active: IconType; inactive: IconType }) {
  return (
    <div className="relative grid grid-cols-1 grid-rows-1 place-items-center">
      <Icon
        className="invisible z-20 col-span-full row-span-full size-3.5 group-aria-[checked=true]/item:visible"
        symbol={active}
      />
      <Icon
        className="text-ds-neutral-6 invisible z-10 col-span-full row-span-full size-3.5 translate-y-px group-aria-[checked=true]/item:visible"
        symbol={active}
      />
      <Icon
        className="invisible z-20 col-span-full row-span-full size-3.5 group-aria-[checked=false]/item:visible"
        symbol={inactive}
      />
    </div>
  );
}
